<svelte:options runes={true} />

<script lang="ts">
  import { HAlignment, horizontalAlignmentForTextDirection, VAlignment } from '@geom';
  import { localeManager } from '@reactive';
  import { TextDirection } from '@strings';
  import {
    APP_ICON_PANEL_DOWN,
    APP_ICON_PANEL_LEFT,
    APP_ICON_PANEL_RIGHT,
    APP_ICON_PANEL_UP,
    type AppIcon
  } from '@ui';
  import { type Snippet } from 'svelte';

  let {
    barSide,
    vBarSide = undefined,
    orientation = 'vertical', // 'vertical' (left/right) or 'horizontal' (top/bottom)
    visible = $bindable(),
    children
  }: {
    barSide?: HAlignment; // required if orientation === 'vertical'
    vBarSide?: VAlignment; // required if orientation === 'horizontal'
    orientation?: 'vertical' | 'horizontal';
    visible: boolean;
    children: Snippet;
  } = $props();

  function handleToggle() {
    visible = !visible;
  }

  /**
   * Helper function that returns the appropriate panel icon based on visibility state
   * and text direction (RTL/LTR). In RTL layouts, the chevron directions are reversed.
   */
  function getPanelIcon(isVisible: boolean, textDirection: TextDirection): AppIcon {
    if (orientation === 'vertical') {
      const resolved = horizontalAlignmentForTextDirection(
        barSide ?? HAlignment.LEFT,
        textDirection
      );
      switch (resolved) {
        case HAlignment.LEFT:
        default:
          return isVisible ? APP_ICON_PANEL_LEFT : APP_ICON_PANEL_RIGHT;
        case HAlignment.RIGHT:
          return isVisible ? APP_ICON_PANEL_RIGHT : APP_ICON_PANEL_LEFT;
      }
    } else {
      // horizontal (top/bottom)
      const side = vBarSide ?? VAlignment.TOP;
      switch (side) {
        case VAlignment.TOP:
        default:
          // Top bar: visible shows collapse (up), hidden shows expand (down)
          return isVisible ? APP_ICON_PANEL_UP : APP_ICON_PANEL_DOWN;
        case VAlignment.BOTTOM:
          // Bottom bar: visible shows collapse (down), hidden shows expand (up)
          return isVisible ? APP_ICON_PANEL_DOWN : APP_ICON_PANEL_UP;
      }
    }
  }

  let toggleIcon = $derived.by<AppIcon>(() => {
    return getPanelIcon(visible, localeManager.textDirection);
  });

  let buttonTitle = $derived(visible ? 'Hide panel' : 'Show panel');

  // Compute icon alignment container justification
  let iconAlignment = $derived.by(() => {
    if (orientation === 'horizontal') {
      // Always keep toggle on the right side for horizontal bars
      return 'flex-end';
    }
    // Vertical behavior: center when hidden, otherwise align based on side
    if (!visible) return 'center';
    const resolvedBarSide = horizontalAlignmentForTextDirection(
      barSide ?? HAlignment.LEFT,
      localeManager.textDirection
    );
    return resolvedBarSide === HAlignment.LEFT ? 'flex-end' : 'flex-start';
  });
</script>

<style>
  .bar-container {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .bar-container.horizontal {
    flex-direction: row;
    width: 100%;
    height: auto;
  }

  .toggle-section {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  .toggle-section.horizontal {
    height: 100%;
    flex-direction: row;
    flex: 0 0 2rem; /* fixed width */
    width: 2rem;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    margin-left: auto; /* push to right even when alone */
  }
  .toggle-section.horizontal.top-align {
    align-items: flex-start;
  }

  .toggle-button {
    --sl-spacing-medium: 0.5rem;
  }

  .toggle-button::part(base) {
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    box-shadow: none !important;
  }

  .toggle-button::part(base):hover {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }

  .toggle-button::part(base):active {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }

  .toggle-button::part(base):focus-visible {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }

  .content-section {
    flex: 1;
    overflow: hidden;
  }

  .content-section.horizontal {
    width: 100%;
  }

  /* When content is hidden, the container becomes very narrow */
  .bar-container:not(:has(.content-section)) {
    width: 1.5rem;
    min-width: 1.5rem;
  }

  .bar-container.horizontal:not(:has(.content-section)) {
    height: 1.5rem;
    min-height: 1.5rem;
    width: 100%;
  }

  /* Ensure toggle section uses full width when horizontal & collapsed to keep button right-aligned */
  /* No special rule needed now; margin-left:auto keeps right alignment when collapsed */
</style>

<div class="bar-container {orientation}">
  {#if visible && orientation === 'horizontal'}
    <div class="content-section {orientation}">
      {@render children()}
    </div>
  {/if}

  <div
    style="justify-content: {iconAlignment};"
    class="toggle-section {orientation} {orientation === 'horizontal' && visible
      ? 'top-align'
      : ''}"
  >
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <sl-icon-button
      name={toggleIcon.name}
      class="toggle-button"
      label={buttonTitle}
      library={toggleIcon.library}
      onclick={handleToggle}
      role="button"
      tabindex="0"
    ></sl-icon-button>
  </div>

  {#if visible && orientation === 'vertical'}
    <div class="content-section {orientation}">
      {@render children()}
    </div>
  {/if}
</div>
